import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  InputAdornment,
  IconButton,
  Alert,
  Dialog,
  CircularProgress,
  useTheme,
  alpha
} from '@mui/material';
import {
  Email as EmailIcon,
  Lock as LockIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  AdminPanelSettings as AdminIcon,
  Security as SecurityIcon
} from '@mui/icons-material';
import { superAdminLogin } from '../services/superAdminAuthService';
import API_CONFIG from '../../config/api.config';
import axios from 'axios';

/**
 * Super Admin Login Page
 * Professional and attractive login interface for Tiya Technology super administrators
 */
const SuperAdminLogin = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [registerOpen, setRegisterOpen] = useState(false);
  const [registerError, setRegisterError] = useState('');
  const [registerLoading, setRegisterLoading] = useState(false);
  const [registerData, setRegisterData] = useState({
    fullName: '',
    username: '',
    email: '',
    password: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
    // Clear error when user starts typing
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await superAdminLogin(formData.username, formData.password);
      
      // Navigate to super admin dashboard
      setTimeout(() => {
        navigate('/superadmin/dashboard');
      }, 100);
    } catch (err) {
      setError(err.message || 'Login failed. Please check your credentials and try again.');
      console.error('Super Admin Login error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: theme.palette.background.default,
        py: 4
      }}
    >
      <Container maxWidth="sm">
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            mb: 4
          }}
        >
          {/* Logo/Icon Section */}
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              background: theme.palette.primary.main,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 3,
              boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.2)}`
            }}
          >
            <AdminIcon sx={{ fontSize: 40, color: 'white' }} />
          </Box>

          <Typography
            variant="h4"
            component="h1"
            align="center"
            sx={{
              fontWeight: 600,
              color: theme.palette.text.primary,
              mb: 1
            }}
          >
            Super Admin Portal
          </Typography>
          <Typography
            variant="subtitle1"
            align="center"
            sx={{
              color: theme.palette.text.secondary,
              mb: 2
            }}
          >
            Tiya Technology Administration
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              justifyContent: 'center',
              mb: 3
            }}
          >
            <SecurityIcon sx={{ color: theme.palette.text.secondary, fontSize: 16 }} />
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.text.secondary,
                fontStyle: 'italic'
              }}
            >
              Secure Access Only
            </Typography>
          </Box>
        </Box>

        <Paper
          elevation={3}
          sx={{
            p: 4,
            borderRadius: 2,
            background: theme.palette.background.paper,
            boxShadow: `0 2px 8px ${alpha('#000000', 0.08)}`,
            border: `1px solid ${alpha(theme.palette.divider, 0.5)}`
          }}
        >
          <Typography
            variant="h5"
            component="h2"
            gutterBottom
            align="center"
            sx={{
              fontWeight: 600,
              mb: 1,
              color: theme.palette.primary.main
            }}
          >
            Administrator Login
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            gutterBottom
            align="center"
            sx={{ mb: 4 }}
          >
            Enter your credentials to access the administration panel
          </Typography>

          {error && (
            <Alert
              severity="error"
              sx={{
                mb: 3,
                borderRadius: 2,
                '& .MuiAlert-icon': {
                  alignItems: 'center'
                }
              }}
            >
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              fullWidth
              id="username"
              name="username"
              label="Username"
              variant="outlined"
              margin="normal"
              value={formData.username}
              onChange={handleChange}
              placeholder="Enter your username"
              required
              disabled={loading}
              autoFocus
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '&:hover fieldset': {
                    borderColor: theme.palette.primary.main
                  },
                  '&.Mui-focused fieldset': {
                    borderWidth: 2
                  }
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon color="action" />
                  </InputAdornment>
                )
              }}
            />

            <TextField
              fullWidth
              id="password"
              name="password"
              label="Password"
              variant="outlined"
              margin="normal"
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={handleChange}
              placeholder="Enter your password"
              required
              disabled={loading}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '&:hover fieldset': {
                    borderColor: theme.palette.primary.main
                  },
                  '&.Mui-focused fieldset': {
                    borderWidth: 2
                  }
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleTogglePasswordVisibility}
                      edge="end"
                      size="small"
                    >
                      {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              size="large"
              disabled={loading || !formData.username || !formData.password}
              sx={{
                mt: 4,
                mb: 2,
                py: 1.5,
                borderRadius: 2,
                fontSize: '1rem',
                fontWeight: 600,
                textTransform: 'none',
                boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.3)}`,
                '&:hover': {
                  boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.4)}`
                }
              }}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Sign In to Admin Panel'
              )}
            </Button>

            <Box sx={{ mt: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Authorized Personnel Only
              </Typography>
              <Box sx={{ mt: 1 }}>
                <Button
                  variant="text"
                  onClick={() => {
                    setRegisterError('');
                    setRegisterOpen(true);
                  }}
                >
                  Register
                </Button>
              </Box>
            </Box>
          </Box>
        </Paper>

        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.text.secondary,
              fontWeight: 400
            }}
          >
            © {new Date().getFullYear()} Tiya Technology. All rights reserved.
          </Typography>
        </Box>
      </Container>
      <Dialog open={registerOpen} onClose={() => setRegisterOpen(false)} maxWidth="xs" fullWidth>
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Super Admin Registration
          </Typography>
          {registerError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {registerError}
            </Alert>
          )}
          <TextField
            fullWidth
            label="Full Name"
            margin="normal"
            value={registerData.fullName}
            onChange={(e) => setRegisterData({ ...registerData, fullName: e.target.value })}
          />
          <TextField
            fullWidth
            label="Username"
            margin="normal"
            value={registerData.username}
            onChange={(e) => setRegisterData({ ...registerData, username: e.target.value })}
          />
          <TextField
            fullWidth
            type="email"
            label="Email"
            margin="normal"
            value={registerData.email}
            onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
          />
          <TextField
            fullWidth
            type={showPassword ? 'text' : 'password'}
            label="Password"
            margin="normal"
            value={registerData.password}
            onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
            <Button onClick={() => setRegisterOpen(false)} disabled={registerLoading}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={async () => {
                setRegisterError('');
                setRegisterLoading(true);
                try {
                  const payload = {
                    username: registerData.username,
                    email: registerData.email,
                    password: registerData.password,
                    fullName: registerData.fullName
                  };
                  await axios.post(`${API_CONFIG.BASE_URL}/superadmin/public/register`, payload, {
                    headers: {
                      'Content-Type': 'application/json',
                      'Accept': 'application/json'
                    }
                  });
                  setFormData({ username: registerData.username, password: '' });
                  setRegisterOpen(false);
                } catch (err) {
                  if (err.response) {
                    const msg = err.response.data?.message || 'Registration failed';
                    setRegisterError(msg);
                  } else if (err.request) {
                    setRegisterError('Network error. Please try again.');
                  } else {
                    setRegisterError(err.message || 'Registration failed');
                  }
                } finally {
                  setRegisterLoading(false);
                }
              }}
              disabled={
                registerLoading ||
                !registerData.fullName ||
                !registerData.username ||
                !registerData.email ||
                !registerData.password
              }
            >
              {registerLoading ? <CircularProgress size={20} color="inherit" /> : 'Register'}
            </Button>
          </Box>
        </Box>
      </Dialog>
    </Box>
  );
};

export default SuperAdminLogin;

